<script lang="ts">
  import * as Sidebar from '$lib/components/ui/sidebar/index'
  import * as Collapsible from '$lib/components/ui/collapsible/index'

  import { Button } from '$lib/components/ui/button/index'
  import { Badge } from '$lib/components/ui/badge/index.js'

  import { goto, route } from '@mateothegreat/svelte5-router'
  import * as pkg from '../../../../package.json'

  import bars from '$lib/assets/icons/bars.svg'
  import favicon from '$lib/assets/icons/favicon.svg'
  import Icon from '$lib/components/nuread/Icon.svelte'
  import { mode } from 'mode-watcher'
  import { currentPath } from '$lib/store'
  import { cn } from '$lib/utils.js'
  import House from '@lucide/svelte/icons/house'
  import SidebarServerMenu from './SidebarServerMenu.svelte'
  import SidebarLibraryMenuItem from './SidebarLibraryMenuItem.svelte'
  import CreateLibrary from './modals/CreateLibrary.svelte'
  import { createLibraryModal } from '$lib/create-library.svelte'

  function getSvgPath(svg: string) {
    const raw = decodeURIComponent(svg.toString())
    const match = raw.match(/\bd=["']([^"']+)["']/)
    if (match) return match[1].replaceAll('%20', ' ')
    return (
      raw
        .split('d=')[1]
        ?.split(/\/?%3e|>/i)[0]
        ?.replaceAll('%20', ' ')
        ?.replaceAll(/['"]/g, '')
        ?.trim() ?? ''
    )
  }

  let { onToggle, servers, libraries, disconnectedServers = {}, refreshLibraries } = $props()

  let colorSwap = $derived(mode.current == 'dark' ? 0 : 1)
  let colors = $derived([
    Math.abs(colorSwap * 255 - 28),
    Math.abs(colorSwap * 255 - 38),
    Math.abs(colorSwap * 255 - 32),
    Math.abs(colorSwap * 255 - 51),
  ])

  // Soft corner washes — low alpha so bg-border stays the dominant tone.
  const gradientStyle = $derived(`
    height: inherit;
    background-color: var(--border);
    background-image:
      radial-gradient(
        circle farthest-side at 0% 100%,
        rgba(${colors[0]}, ${colors[0]}, ${colors[0]}, 0.22) 0%,
        rgba(${colors[0]}, ${colors[0]}, ${colors[0]}, 0) 72%
      ),
      radial-gradient(
        circle farthest-side at 100% 100%,
        rgba(${colors[1]}, ${colors[1]}, ${colors[1]}, 0.18) 0%,
        rgba(${colors[1]}, ${colors[1]}, ${colors[1]}, 0) 68%
      ),
      radial-gradient(
        circle farthest-side at 100% 0%,
        rgba(${colors[2]}, ${colors[2]}, ${colors[2]}, 0.16) 0%,
        rgba(${colors[2]}, ${colors[2]}, ${colors[2]}, 0) 70%
      ),
      radial-gradient(
        circle farthest-side at 0% 0%,
        rgba(${colors[3]}, ${colors[3]}, ${colors[3]}, 0.14) 0%,
        rgba(${colors[3]}, ${colors[3]}, ${colors[3]}, 0) 65%
      );
  `)

  function openCreateNewLibrary(serverid: string) {
    createLibraryModal.serverid = serverid
    createLibraryModal.open = true
  }

  const homeActive = $derived(
    $currentPath === '/' || $currentPath === '',
  )
</script>

<Sidebar.Root class="mt-2 z-45 [&_[data-sidebar=sidebar]]:bg-border [&_[data-slot=sidebar-inner]]:bg-border">
  <Sidebar.Header
    class="gap-0 border-b border-latte-surface1/50 bg-border px-2 py-2.5
      dark:border-macchiato-surface1/50"
  >
    <div class="flex items-center gap-0.5">
      <Button
        class="size-9 shrink-0 text-latte-text/70 hover:text-latte-text dark:text-macchiato-text/70 dark:hover:text-macchiato-text"
        variant="ghost"
        size="icon"
        onclick={() => onToggle()}
      >
        <Icon viewBox="0 0 640 640" class="size-5">
          <path d={getSvgPath(bars)} />
        </Icon>
        <span class="sr-only">Toggle sidebar</span>
      </Button>
      <button
        type="button"
        class="group flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5 text-start transition-colors
          hover:bg-latte-surface1/40 dark:hover:bg-macchiato-surface1/40"
        onclick={() => goto('/')}
      >
        <Icon
          viewBox="0 0 640 512"
          class="size-7 shrink-0 text-latte-green transition-transform duration-300 group-hover:scale-105 dark:text-macchiato-green"
        >
          <path fill-rule="evenodd" d={getSvgPath(favicon)} />
        </Icon>
        <span class="truncate text-base font-semibold tracking-tight text-latte-text dark:text-macchiato-text">
          nuread
        </span>
      </button>
    </div>
  </Sidebar.Header>

  <Sidebar.Content style={gradientStyle}>
    <Sidebar.Group class="px-0.5 pt-2 pb-2">
      <Sidebar.GroupContent>
        <Sidebar.Menu class="gap-0.5">
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              isActive={homeActive}
              class="h-auto rounded-lg px-1 py-2
                hover:bg-latte-surface1/35 dark:hover:bg-macchiato-surface1/35
                data-[active=true]:bg-latte-surface1/50 dark:data-[active=true]:bg-macchiato-surface1/40"
              size="lg"
            >
              {#snippet child({ props })}
                <div class="flex w-full items-center gap-0.5">
                  <!-- Match server row chevron column so icon+label align -->
                  <span class="size-5 shrink-0" aria-hidden="true"></span>
                  <a
                    href="/"
                    {...props}
                    class={cn(
                      'flex min-w-0 flex-1 items-center gap-2',
                      props?.class,
                    )}
                    use:route
                  >
                    <span
                      class="flex size-8 shrink-0 items-center justify-center rounded-full
                        ring-1 ring-latte-surface1/80 dark:ring-macchiato-surface1/80
                        {homeActive
                          ? 'ring-latte-green/80 ring-offset-1 ring-offset-transparent dark:ring-macchiato-green/80'
                          : ''}"
                    >
                      <House
                        class="size-4 {homeActive
                          ? 'text-latte-green dark:text-macchiato-green'
                          : 'text-latte-text/70 dark:text-macchiato-text/70'}"
                        strokeWidth={1.75}
                      />
                    </span>
                    <span
                      class="truncate text-sm font-medium leading-tight
                        {homeActive
                          ? 'text-latte-green dark:text-macchiato-green'
                          : 'text-latte-text dark:text-macchiato-text'}"
                    >
                      Home
                    </span>
                  </a>
                </div>
              {/snippet}
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          {#each servers as item (item.uid)}
            <Collapsible.Root open class="group/collapsible">
              <Sidebar.MenuItem>
                <Sidebar.MenuButton
                  class="h-auto rounded-lg px-1 py-2
                    hover:bg-latte-surface1/35 dark:hover:bg-macchiato-surface1/35
                    data-[active=true]:bg-latte-surface1/50 dark:data-[active=true]:bg-macchiato-surface1/40"
                  size="lg"
                >
                  {#snippet child({ props })}
                    <SidebarServerMenu
                      {props}
                      {item}
                      {openCreateNewLibrary}
                      disconnected={!!disconnectedServers?.[item.uid]}
                    />
                  {/snippet}
                </Sidebar.MenuButton>

                <Collapsible.Content>
                  <Sidebar.MenuSub
                    class="mx-1 mb-1 mt-0.5 ms-5 border-latte-surface1/50 py-1 dark:border-macchiato-surface1/50"
                  >
                    {#each libraries?.[item.uid] ?? [] as lib (lib.id)}
                      <Sidebar.MenuSubItem>
                        <Sidebar.MenuButton
                          class="h-auto rounded-md py-1.5
                            hover:bg-latte-surface1/30 dark:hover:bg-macchiato-surface1/30"
                        >
                          {#snippet child({ props })}
                            <SidebarLibraryMenuItem {props} {item} {lib} {libraries} />
                          {/snippet}
                        </Sidebar.MenuButton>
                      </Sidebar.MenuSubItem>
                    {:else}
                      <li class="px-2 py-1.5 text-xs text-latte-text/40 dark:text-macchiato-text/40">
                        {#if disconnectedServers?.[item.uid]}
                          Unreachable
                        {:else if libraries?.[item.uid] === undefined}
                          Loading…
                        {:else}
                          No libraries yet
                        {/if}
                      </li>
                    {/each}
                  </Sidebar.MenuSub>
                </Collapsible.Content>
              </Sidebar.MenuItem>
            </Collapsible.Root>
          {:else}
            <li class="px-3 py-6 text-center text-sm text-latte-text/45 dark:text-macchiato-text/45">
              No servers yet
            </li>
          {/each}
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>
  </Sidebar.Content>

  <Sidebar.Footer
    class="border-t border-latte-surface1/50 bg-border px-3 py-3
      dark:border-macchiato-surface1/50"
  >
    <div class="rounded-lg px-2.5 py-2">
      <div class="flex items-center gap-2">
        <Badge
          class="h-5 rounded px-1.5 font-semibold bg-latte-base text-latte-text
            dark:bg-macchiato-surface1 dark:text-macchiato-text"
          variant="secondary"
        >
          Beta {pkg?.version}
        </Badge>
      </div>
      <p class="mt-1.5 text-[0.7rem] leading-relaxed text-latte-text/55 dark:text-macchiato-text/55">
        Preview of
        <span class="font-medium text-latte-text/80 dark:text-macchiato-text/80">nuread</span>.
        Everything here is subject to change.
      </p>
      <Button
        href="https://github.com/raiflhem/nuread"
        class="mt-0.5 h-auto px-0 text-[0.7rem] font-normal text-latte-text/50 hover:text-latte-green
          dark:text-macchiato-text/50 dark:hover:text-macchiato-green"
        variant="link"
      >
        source repo
      </Button>
    </div>
  </Sidebar.Footer>
</Sidebar.Root>

{#if createLibraryModal.serverid}
  <CreateLibrary
    bind:open={createLibraryModal.open}
    serverid={createLibraryModal.serverid}
    {refreshLibraries}
  />
{/if}
